import { FC, useMemo } from "react";
import { useSelector } from "react-redux";

import { SelectionCard } from "../../common";
import { NewWalletAction } from "../../actions";
import {
  CURRENCY_MAP,
  DEFAULT_CRYPTO_CURRENCY,
  uiConstants,
} from "../../../constants";
import { useAllowedChains } from "../../../hooks/useAllowedChains";
import { useTriggerToastOnRTKError } from "../../../hooks/useTriggerToastOnRTKError";
import { ScrollableSection } from "../../../layouts/ScrollableSection";
import { selectInvoice } from "../../../store/reducers/invoice";
import { selectCurrentUser } from "../../../store/reducers/user";
import { selectWallets } from "../../../store/reducers/wallets";
import { useUpdateInvoiceMutation } from "../../../store/services/invoices";
import {
  CurrencyType,
  InvoiceStatus,
  OutgoingPaymentDetails,
} from "../../../types/InvoiceTypes";
import { Chain, Wallet } from "../../../types/WalletTypes";
import {
  StatusMessageValue,
  validateWalletOnmetaStatus,
} from "../../../utils/wallets";

type Props = {
  onSelect?: (wallet: Wallet) => void;
};

const walletTypeName = {
  [CurrencyType.CRYPTO]: "wallet",
  [CurrencyType.FIAT]: "bank account",
};

const getCardTitle = (wallet: Wallet | OutgoingPaymentDetails) => {
  return wallet.address
    ? `${wallet.chain} - ${wallet.name}`
    : `${wallet.bank_name || wallet.bank_code || ""} - ${wallet.name}`;
};

const getCardValue = (wallet: Wallet | OutgoingPaymentDetails) => {
  return wallet.address ? wallet.address! : wallet.account_number!;
};

const isCardDisabled = (wallet: Wallet, supportedChains?: Chain[]) => {
  return !supportedChains?.includes(wallet.chain);
};

const isCardSelected = (
  wallet: Wallet,
  paymentDetails?: OutgoingPaymentDetails
) => {
  return wallet.address
    ? wallet.address === paymentDetails?.address
    : wallet.bank_name === paymentDetails?.bank_name &&
        wallet.account_number === paymentDetails?.account_number;
};

export const WalletListForm: FC<Props> = ({ onSelect }) => {
  const invoice = useSelector(selectInvoice);
  const walletList = useSelector(selectWallets);
  const [updateInvoice, { isError, error }] = useUpdateInvoiceMutation();
  useTriggerToastOnRTKError(isError, error);

  const {
    kyc_complete_and_approved,
    is_onmeta_eligible,
    onmeta_kyc_complete_and_approved,
  } = useSelector(selectCurrentUser);

  const outgoingCurrency =
    invoice?.outgoing_currency || DEFAULT_CRYPTO_CURRENCY;

  const outgoingCurrencyDetails = CURRENCY_MAP[outgoingCurrency];
  const allowedChains = useAllowedChains(outgoingCurrency);

  const allowedWallets = useMemo(
    () => walletList.filter(({ chain }) => allowedChains.includes(chain!)),
    [walletList, allowedChains]
  );

  if (!invoice) {
    return <></>;
  }

  const { id, outgoing_payment_details: paymentDetails, status } = invoice;
  const isReadyView = status !== InvoiceStatus.DRAFT;

  const onSelectWallet = async (data: Wallet) => {
    if (outgoingCurrencyDetails.supportedChains?.includes(data?.chain)) {
      await updateInvoice({
        id,
        outgoing_payment_details: {
          wallet_id: data.id,
        },
      });

      onSelect && onSelect(data);
    }
  };

  const AddWallet = (
    <NewWalletAction
      callback={(wallet: any) => onSelectWallet(wallet)}
      currencyType={outgoingCurrencyDetails.type}
    >
      {(action) => (
        <SelectionCard
          icon="Add"
          title={`Add new ${walletTypeName[outgoingCurrencyDetails.type] || "wallet"}`}
          value={`To get payments in ${outgoingCurrencyDetails.code}`}
          onClick={action}
        />
      )}
    </NewWalletAction>
  );

  return (
    <ScrollableSection id="invoice-wallet-form">
      <div className="flex flex-col items-start mb-8 md:mb-12">
        <div className="hidden md:block font-source-serif text-5xl mb-6">
          Select {walletTypeName[outgoingCurrencyDetails.type]}
        </div>

        <div className="theme-text-secondary">
          We don’t share these details with others
        </div>
      </div>

      {isReadyView ? (
        <div className="flex flex-row gap-x-12">
          <SelectionCard
            title={getCardTitle(paymentDetails!)}
            value={getCardValue(paymentDetails!)}
            className={
              "outline-2 outline-green-300 bg-green-50 cursor-none pointer-events-none"
            }
            onClick={() => {}}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-y-4 basis-[528px] max-w-[528px]">
          {allowedWallets.map((wallet) => {
            const isSelected = isCardSelected(wallet, paymentDetails);
            const uiStatus = validateWalletOnmetaStatus(wallet);
            const _uiStatus = !!uiStatus
              ? (uiStatus as StatusMessageValue)
              : undefined;
            const uiStatusStyles = _uiStatus
              ? uiConstants[_uiStatus[0]]
              : undefined;

            return (
              <div
                className="flex flex-col gap-2"
                key={`${wallet.chain}-${getCardValue(wallet)}`}
              >
                <SelectionCard
                  title={getCardTitle(wallet)}
                  value={getCardValue(wallet)}
                  disabled={isCardDisabled(
                    wallet,
                    outgoingCurrencyDetails?.supportedChains
                  )}
                  className={
                    isSelected ? "!border-2 !border-green-300 !bg-green-50" : ""
                  }
                  onClick={() => onSelectWallet(wallet)}
                  uiStatus={_uiStatus?.[0]}
                />

                {_uiStatus && isSelected && (
                  <div className={uiStatusStyles?.textColor}>
                    {typeof _uiStatus[1] === "string"
                      ? _uiStatus[1]
                      : _uiStatus[1](
                          kyc_complete_and_approved ||
                            (is_onmeta_eligible
                              ? onmeta_kyc_complete_and_approved
                              : true)
                        )}
                  </div>
                )}
              </div>
            );
          })}

          {AddWallet}
        </div>
      )}
    </ScrollableSection>
  );
};
